// Service worker registration, update prompt and logout clean-up (plan §8). Production builds only:
// in dev, MSW owns the service-worker slot.
import { clearPushIdentity } from './pushIdentity';
import { onLogout } from './session';
import { toast } from '../ui/Toast';

let waiting: ServiceWorker | null = null;

function promptUpdate(sw: ServiceWorker) {
  waiting = sw;
  toast('A new version of Workdesk is ready.', 'info', {
    action: { label: 'Reload', onClick: applyUpdate },
  });
}

/** The user chose to update: the waiting worker takes over, then we reload once. Never automatic. */
export function applyUpdate() {
  if (!waiting) return window.location.reload();
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
  waiting.postMessage({ type: 'skipWaiting' });
}

export function registerServiceWorker() {
  // Logout clean-up is registered in every mode: identity record, shown notifications, push subscription.
  onLogout(async () => {
    await clearPushIdentity();
    try {
      const reg = await navigator.serviceWorker?.getRegistration();
      reg?.active?.postMessage({ type: 'logout' });
      const sub = await reg?.pushManager?.getSubscription();
      await sub?.unsubscribe();
    } catch {
      /* best effort */
    }
  });
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((reg) => {
        if (reg.waiting && navigator.serviceWorker.controller) promptUpdate(reg.waiting);
        reg.addEventListener('updatefound', () => {
          const nw = reg.installing;
          nw?.addEventListener('statechange', () => {
            if (nw.state === 'installed' && navigator.serviceWorker.controller) promptUpdate(nw);
          });
        });
        // Check for a new deploy when the app comes back to the foreground, at most hourly.
        let last = Date.now();
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible' && Date.now() - last > 3_600_000) {
            last = Date.now();
            void reg.update().catch(() => undefined);
          }
        });
      })
      .catch(() => undefined);
  });
}
